import React from 'react';
import moment from 'moment';
import { get, capitalize, startCase, kebabCase } from 'lodash';
import { Link } from 'react-router-dom';
import {
  StoreStep,
  OnlineStep,
  FinanceType,
  CarStatus,
  DealResult,
  TestDriveDeliveryMethod,
} from '@upstart/harmony/lib/constants';
import {
  fmtMoney,
  fmtNumber,
  fmtPercent,
} from '@upstart/harmony/lib/formatter';
import { formatTimestampToDealerTime } from 'util/utils';
import { OnlinePseudoSteps } from 'util/constants';
import { getVINForDisplay } from '@upstart/harmony/lib/car-utils';

export const SIZE_PER_PAGE = 10;
const HOUR_MINUTES = 60;

export const getTimeLabelFromMinutes = num => {
  const hours = Math.floor(num / HOUR_MINUTES);
  const minutes = num % HOUR_MINUTES;
  const arr = [];
  if (hours) {
    arr.push(`${hours} hr`);
  }
  if (minutes || !arr.length) {
    arr.push(`${minutes} min`);
  }
  return arr.join(' ');
};

export const CLOSERS_TABLE = {
  columns: [
    {
      label: 'Name',
      dataFormat: (_, row) => `${row.firstName} ${row.lastName}`,
    },
    {
      label: 'No. of Deals',
      dataAlign: 'right',
      dataField: 'totalCount',
      width: '100px',
    },
    {
      label: 'No. of Sold Deals',
      dataAlign: 'right',
      dataField: 'soldCount',
      width: '100px',
    },
    {
      label: 'Close Rate',
      dataAlign: 'right',
      dataFormat: (_, row) =>
        `${Number(row.closeRate * 100)
          .toFixed(2)
          .replace(/\.0+$/, '')}%`,
      width: '100px',
    },
  ],
};

export const PROVIDER_TABLE = {
  columns: [
    {
      label: 'Source',
      dataFormat: (cell, row) => row[0],
    },
    {
      label: 'Enhances',
      dataAlign: 'right',
      dataFormat: (cell, row) => row[1],
    },
  ],
};

export const getSalesPersonTable = dealer => {
  const table = {
    options: {
      sortName: 'total',
      sortOrder: 'desc',
    },
    columns: [
      {
        label: 'Salesperson',
        dataField: 'name',
        dataFormat: (cell, row) => (
          <Link
            to={{
              pathname: `/results/${row.id}`,
              search: window.location.search,
            }}
          >
            {cell}
          </Link>
        ),
      },
      {
        label: 'Total Deals',
        dataField: 'total',
        dataAlign: 'center',
      },
      {
        label: 'Sold Deals',
        dataField: 'sold',
        dataAlign: 'center',
      },
      {
        label: 'Customer Info',
        dataField: StoreStep.customerInfo,
        dataAlign: 'center',
      },
      {
        label: 'Vehicle Selected',
        dataField: StoreStep.vehicleSelection,
        dataAlign: 'center',
      },
      {
        label: 'DL Scan',
        dataField: StoreStep.driverLicense,
        dataAlign: 'center',
      },
      {
        label: 'Document Scan',
        dataField: StoreStep.documentScan,
        dataAlign: 'center',
      },
      {
        label: 'Manual Completed Test Drives',
        dataField: 'manualTestDrive',
        dataAlign: 'center',
      },
      {
        label: 'Completed Test Drives',
        dataField: StoreStep.automaticTestDrive,
        dataAlign: 'center',
      },
      {
        label: 'Trade-in',
        dataField: StoreStep.tradeIn,
        dataAlign: 'center',
      },
      {
        label: 'Credit App',
        dataField: StoreStep.creditApplication,
        dataAlign: 'center',
      },
      {
        label: 'Printed',
        dataField: StoreStep.printed,
        dataAlign: 'center',
      },
      {
        label: 'Accessories',
        dataField: StoreStep.accessories,
        dataAlign: 'center',
      },
      {
        label: 'First Pencils Sent',
        dataField: 'firstPencilsReceived',
        dataAlign: 'center',
      },
      {
        label: 'First Pencils Accepted',
        dataField: 'firstPencilsAccepted',
        dataAlign: 'center',
      },
      {
        label: 'Protection Plan',
        dataField: StoreStep.protectionPlans,
        dataAlign: 'center',
      },
    ],
  };

  if (dealer?.insurance?.instore?.enabled) {
    table.columns.push({
      label: 'Insurance Applications',
      dataField: StoreStep.insurance,
      dataAlign: 'center',
    });
  }

  if (dealer?.tabletFormsConfig?.customerInfo?.showInstoreOnlineLink) {
    table.columns.push({
      label: 'Deals Sent',
      dataField: 'sent',
      dataAlign: 'center',
    });
  }

  return table;
};

export const getOnlineTotalsTable = dealer => {
  const table = {
    columns: [
      {
        label: 'Lead Form Captured',
        dataAlign: 'center',
        dataField: OnlineStep.dealStart,
      },
      {
        label: 'Vehicle Selection',
        dataAlign: 'center',
        dataField: OnlineStep.vehicleSelection,
      },
      {
        dataAlign: 'center',
        dataField: OnlineStep.tradeIn,
        label: 'Trade-in Added',
      },
      {
        label: 'Customize Payments',
        dataAlign: 'center',
        dataField: OnlineStep.paymentBuilder,
      },
      {
        label: 'Soft Credit Pulls',
        dataAlign: 'center',
        dataField: OnlineStep.softCreditPull,
      },
      {
        label: 'Pre-approved',
        dataAlign: 'center',
        dataField: OnlineStep.creditApplication,
      },
      {
        label: 'Protection Plan',
        dataAlign: 'center',
        dataField: OnlineStep.upgradePackages,
      },
      {
        label: 'Schedule Test Drive',
        dataAlign: 'center',
        dataField: OnlineStep.testDrive,
      },
      {
        label: 'Accessories',
        dataAlign: 'center',
        dataField: OnlineStep.accessories,
      },
      // Custom fields to seperate Delivery and Pickup
      {
        label: 'Pickup',
        dataAlign: 'center',
        dataField: OnlinePseudoSteps.pickup,
      },
      {
        label: 'Delivery',
        dataAlign: 'center',
        dataField: OnlinePseudoSteps.delivery,
      },
    ],
  };

  if (dealer?.insurance?.online?.enabled) {
    table.columns.push({
      label: 'Insurance Applications',
      dataField: OnlineStep.insurance,
      dataAlign: 'center',
    });
  }

  return table;
};

const formatDate = cell => moment(cell).format('MM-DD-YY h:mm a');

const formatStatus = cell =>
  cell ? <div className="check-icon" /> : <span>–</span>;

const renderName = (cell, { Deal }) => {
  if (Deal && Deal.applicant) {
    return `${Deal.applicant.firstName} ${Deal.applicant.lastName}`;
  }
  return '';
};

// eslint-disable-next-line react/prop-types
const renderNameDetails = ({ Deal: deal }) => (
  <>
    <span className="results-deals-label">Email:</span>{' '}
    {get(deal, 'applicant.email') || 'N/A'} <br />
    <span className="results-deals-label">Phone:</span>{' '}
    {get(deal, 'applicant.phone') || 'N/A'} <br />
  </>
);

const renderDateForDeliveryOrPickup = (row, label, deliveryAddress, dealer) => {
  const deliveryDate = get(row, 'Deal.pickupSelectedDateTime', '');
  if (!deliveryDate) {
    return null;
  }
  return (
    <div>
      <div className="results-deals-label">{label}:</div>
      <div>{formatTimestampToDealerTime(deliveryDate, dealer)}</div>
      {deliveryAddress && (
        <>
          <div className="results-deals-label">Delivery Address:</div>
          <div>{deliveryAddress}</div>
        </>
      )}
    </div>
  );
};

const renderPickupDate = (row, { dealer }) => {
  const { Deal, schedulePickup } = row;
  const isPickup =
    get(Deal, 'deliveryMethod', null) === OnlinePseudoSteps.pickup;
  return schedulePickup && isPickup
    ? renderDateForDeliveryOrPickup(row, 'Pickup Date', null, dealer)
    : null;
};

const renderDeliveryDate = (row, { dealer }) => {
  const { Deal, schedulePickup } = row;
  const isDelivvery =
    get(Deal, 'deliveryMethod', null) === OnlinePseudoSteps.delivery;
  const deliveryAddress = get(Deal, 'deliveryDetails.address', null);
  return schedulePickup && isDelivvery
    ? renderDateForDeliveryOrPickup(
        row,
        'Delivery Date',
        deliveryAddress,
        dealer,
      )
    : null;
};

// eslint-disable-next-line react/prop-types
const renderCarDetails = ({ Deal: deal }) => {
  const vin = get(deal, 'car_id');
  const stock = get(deal, 'car.stock');
  return (
    <>
      {get(deal, 'car.name')} <br />
      {stock ? (
        <>
          <span className="results-deals-label">Stock:</span> {stock}
        </>
      ) : (
        vin && (
          <>
            <span className="results-deals-label">VIN:</span>{' '}
            {getVINForDisplay(vin)}
          </>
        )
      )}
    </>
  );
};

// eslint-disable-next-line react/prop-types
const renderSoldCarCell = ({ Deal: deal }) => {
  if (!deal || deal.result !== DealResult.SOLD) return <span>–</span>;
  const { car } = deal;
  const { name, stock } = car || {};
  return (
    <div className="results-deals-vehicle-sold-cell">
      {name}
      <br />
      {stock && (
        <>
          <span>Stock:</span> {stock}
        </>
      )}
    </div>
  );
};

// eslint-disable-next-line react/prop-types
const renderTradeInDetails = ({ Deal: deal }) => {
  if (!get(deal, 'tradeIns.0')) return null;

  return (
    <>
      <span className="results-deals-label">Year:</span>{' '}
      {get(deal, 'tradeIns.0.year.name')} <br />
      <span className="results-deals-label">Model:</span>{' '}
      {`${get(deal, 'tradeIns.0.make.name')} ${get(
        deal,
        'tradeIns.0.model.name',
      )}`}
      <br />
      <span className="results-deals-label">Miles:</span>{' '}
      {fmtNumber(get(deal, 'tradeIns.0.mileage'))} <br />
      <span className="results-deals-label">Zip:</span>{' '}
      {get(deal, 'tradeIns.0.zip')} <br />
      <span className="results-deals-label">Evaluation:</span>{' '}
      {fmtMoney(get(deal, 'tradeIns.0.tradeValue'))} <br />
    </>
  );
};

// eslint-disable-next-line react/prop-types
const renderTradeInsDetails = ({ Deal: deal }) =>
  (get(deal, 'tradeIns') || []).map((tradeIn, index) => (
    // eslint-disable-next-line react/no-array-index-key
    <React.Fragment key={index}>
      {get(tradeIn, 'year.name')} {get(tradeIn, 'make.name')}{' '}
      {get(tradeIn, 'model.name')}
      <br />
    </React.Fragment>
  ));

// eslint-disable-next-line react/prop-types
const renderOfferDetails = ({ Deal: deal }) => {
  if (!get(deal, 'selectedOffer')) return null;
  const financeType = get(deal, 'financeType');
  const zip = get(deal, 'applicant.zip');
  const isOnePayLease = deal?.onePayLease && financeType === FinanceType.LEASE;
  return (
    <>
      <span className="results-deals-label">Purchase Type:</span>{' '}
      {capitalize(financeType)} <br />
      {financeType !== FinanceType.CASH && (
        <>
          <span className="results-deals-label">Term:</span>{' '}
          {get(deal, 'selectedOffer.months')} <br />
        </>
      )}
      {!!isOnePayLease && (
        <>
          <div>One Pay Lease</div> <br />
        </>
      )}
      {financeType === FinanceType.LEASE && (
        <>
          <span className="results-deals-label">Mileage:</span>{' '}
          {fmtNumber(get(deal, 'selectedOffer.mileage'))} <br />
        </>
      )}
      {financeType !== FinanceType.CASH && !isOnePayLease && (
        <>
          <span className="results-deals-label">Down Payment:</span>{' '}
          {fmtMoney(get(deal, 'downPayment'))} <br />
        </>
      )}
      {financeType !== FinanceType.CASH &&
        !isOnePayLease &&
        deal?.selectedOffer?.estimatedMonthlyPayment && (
          <>
            <span className="results-deals-label">Estimated Payment:</span>{' '}
            {fmtMoney(get(deal, 'selectedOffer.estimatedMonthlyPayment'))}{' '}
            <br />
          </>
        )}
      {isOnePayLease && (
        <>
          <span className="results-deals-label">Total Due:</span>{' '}
          {fmtMoney(get(deal, 'selectedOffer.onePayment'))} <br />
        </>
      )}
      {zip && (
        <>
          <span className="results-deals-label">Zip:</span> {zip}
        </>
      )}
    </>
  );
};

// eslint-disable-next-line react/prop-types
const renderCreditPullDetails = ({ Deal: deal }) => {
  if (!get(deal, 'creditPull.note')) return null;
  return (
    <>
      <span className="results-deals-label">Successfully checked</span>
      <br />
      {get(deal, 'creditPull.creditTier.low')}-
      {get(deal, 'creditPull.creditTier.high')}
    </>
  );
};

// eslint-disable-next-line react/prop-types
const renderCreditPushDetails = ({ Deal: deal }) => {
  const routeOneSubmitted = !!get(deal, 'financeMeta.routeOneSubmissionHash');
  const dealerTrackSubmitted = !!get(deal, 'financeMeta.dealertrackRefNumber');
  const routeOneIdExist = !!get(deal, 'financeMeta.routeOneId');
  const routeOneId = get(deal, 'financeMeta.routeOneId');

  if (!routeOneSubmitted && !dealerTrackSubmitted) return null;

  return (
    <>
      <span className="results-deals-label">Successfully sent to</span>
      <br />
      {routeOneSubmitted && 'RouteOne'} {dealerTrackSubmitted && 'DealerTrack'}
      <br />
      {routeOneIdExist && (
        <>
          <span className="results-deals-label">RouteOne ID:</span>
          <br />
          {routeOneId}
        </>
      )}
    </>
  );
};

// eslint-disable-next-line react/prop-types
const renderProtectionPlanDetails = ({ Deal: deal }, { dealer }) => {
  const selectedProducts = get(deal, 'selectedProducts') || [];
  if (!selectedProducts.length) return null;
  return (
    <>
      <span className="results-deals-label">Selected:</span>
      <br />
      {selectedProducts
        .map(
          ({ productId }) =>
            (dealer.penProducts.find(product => product.id === productId) || {})
              .displayName,
        )
        .join(', ')}
    </>
  );
};

// eslint-disable-next-line react/prop-types
const renderScheduleTestDriveDetails = ({ Deal: deal }, { dealer }) => {
  if (!deal.testDriveSelectedDateTime) return null;

  const time = moment(deal.testDriveSelectedDateTime).tz(dealer.timezone);
  return (
    <>
      <span className="results-deals-label">Selected Date:</span>{' '}
      {time.format('Do MMM')} <br />
      <span className="results-deals-label">Selected Time:</span>{' '}
      {time.format('hh:mm a')} <br />
      {deal?.testDriveDeliveryMethod === TestDriveDeliveryMethod.REMOTE && (
        <>
          <span className="results-deals-label">Remote</span> <br />
          <span className="results-deals-label">Location:</span>{' '}
          {deal?.testDriveDeliveryAddress} <br />
        </>
      )}
    </>
  );
};

export const getOnlineDealsTable = dealer => {
  const table = {
    columns: [
      {
        dataFormat: renderName,
        label: 'Name',
        expandedCellFormat: renderNameDetails,
      },
      {
        dataField: 'updatedAt',
        label: 'Dated',
        dataFormat: formatDate,
        dataAlign: 'center',
        width: '80px',
      },
      {
        dataField: OnlineStep.vehicleSelection,
        label: 'Vehicle Selection',
        dataFormat: formatStatus,
        dataAlign: 'center',
        expandedCellFormat: renderCarDetails,
      },
      {
        dataField: OnlineStep.tradeIn,
        label: 'Trade-in',
        dataFormat: formatStatus,
        dataAlign: 'center',
        expandedCellFormat: renderTradeInDetails,
      },
      {
        dataField: OnlineStep.paymentBuilder,
        label: 'Customize Payments',
        dataFormat: formatStatus,
        dataAlign: 'center',
        expandedCellFormat: renderOfferDetails,
      },
      {
        dataField: OnlineStep.softCreditPull,
        label: 'Instant Credit',
        dataFormat: formatStatus,
        dataAlign: 'center',
        expandedCellFormat: renderCreditPullDetails,
      },
      {
        dataField: OnlineStep.creditApplication,
        label: 'Pre-approved',
        dataFormat: formatStatus,
        dataAlign: 'center',
        expandedCellFormat: renderCreditPushDetails,
      },
      {
        dataField: OnlineStep.upgradePackages,
        label: 'Protection plan',
        dataFormat: formatStatus,
        dataAlign: 'center',
        expandedCellFormat: renderProtectionPlanDetails,
      },
      {
        dataField: OnlineStep.testDrive,
        label: 'Schedule Test Drive',
        dataFormat: formatStatus,
        dataAlign: 'center',
        expandedCellFormat: renderScheduleTestDriveDetails,
      },
      {
        dataField: OnlineStep.accessories,
        label: 'Accessories',
        dataFormat: formatStatus,
        dataAlign: 'center',
      },
      {
        dataField: OnlinePseudoSteps.pickup,
        label: 'Pickup',
        dataFormat: formatStatus,
        dataAlign: 'center',
        expandedCellFormat: renderPickupDate,
      },
      {
        dataField: OnlinePseudoSteps.delivery,
        label: 'Delivery',
        dataFormat: formatStatus,
        dataAlign: 'center',
        expandedCellFormat: renderDeliveryDate,
      },
    ],
  };
  if (dealer?.insurance?.online?.enabled) {
    table.columns.push({
      label: 'Insurance Applications',
      dataField: OnlineStep.insurance,
      dataFormat: formatStatus,
      dataAlign: 'center',
    });
  }
  if (dealer?.lending?.online?.enabled) {
    table.columns.push({
      label: 'Lending Approval',
      dataFormat: (_, { Deal }) => formatStatus(!!Deal?.financeResponse),
      // eslint-disable-next-line react/prop-types
      expandedCellFormat: ({ Deal: deal }) => (
        <>
          <span className="results-deals-label">Provider:</span>{' '}
          {deal?.financeResponse?.financeSourceName} <br />
          <span className="results-deals-label">APR:</span>{' '}
          {fmtPercent(deal?.financeResponse?.data?.apr ?? 0)} <br />
          <span className="results-deals-label">Months:</span>{' '}
          {deal?.financeResponse?.data?.months ?? 0} <br />
          <span className="results-deals-label">Payment:</span>{' '}
          {fmtMoney(deal?.financeResponse?.data?.monthly ?? 0)} <br />
        </>
      ),
      dataAlign: 'center',
    });
  }
  table.columns.push({
    label: 'Vehicle Sold',
    dataField: DealResult.SOLD,
    dataFormat: (_, { Deal }) => renderSoldCarCell({ Deal }),
    dataAlign: 'center',
  });
  return table;
};

export const getStoreDealsTable = dealer => {
  const table = {
    columns: [
      {
        dataFormat: renderName,
        label: 'Name',
        expandedCellFormat: renderNameDetails,
      },
      {
        dataField: 'updatedAt',
        label: 'Dated',
        dataFormat: formatDate,
        dataAlign: 'center',
        width: '80px',
      },
      {
        dataField: StoreStep.customerInfo,
        label: 'Customer Info',
        dataFormat: formatStatus,
        dataAlign: 'center',
      },
      {
        label: 'Vehicle Selected',
        dataField: StoreStep.vehicleSelection,
        dataFormat: formatStatus,
        dataAlign: 'center',
        expandedCellFormat: renderCarDetails,
      },
      {
        label: 'Credit App',
        dataField: StoreStep.creditApplication,
        dataFormat: formatStatus,
        dataAlign: 'center',
        expandedCellFormat: renderCreditPushDetails,
      },
      {
        label: 'DL Scan',
        dataField: StoreStep.driverLicense,
        dataFormat: formatStatus,
        dataAlign: 'center',
      },
      {
        label: 'Document Scan',
        dataField: StoreStep.documentScan,
        dataFormat: formatStatus,
        dataAlign: 'center',
      },
      {
        label: 'Printed',
        dataField: StoreStep.printed,
        dataFormat: formatStatus,
        dataAlign: 'center',
      },
      {
        label: 'Test Drive',
        dataField: StoreStep.testDrive,
        dataFormat: formatStatus,
        dataAlign: 'center',
      },
      {
        label: 'Trade-in',
        dataField: StoreStep.tradeIn,
        dataFormat: formatStatus,
        dataAlign: 'center',
        expandedCellFormat: renderTradeInsDetails,
      },
      {
        label: 'Customize Payments',
        dataFormat: (_, { Deal }) => formatStatus(!!get(Deal, 'selectedOffer')),
        dataAlign: 'center',
        expandedCellFormat: renderOfferDetails,
      },
      {
        label: 'Accessories',
        dataField: StoreStep.accessories,
        dataFormat: formatStatus,
        dataAlign: 'center',
      },
      {
        label: 'Protection Plan',
        dataAlign: 'center',
        dataFormat: (_, { Deal }) =>
          formatStatus(!!get(Deal, 'selectedProducts.0')),
        expandedCellFormat: renderProtectionPlanDetails,
      },
    ],
  };

  if (dealer?.insurance?.instore?.enabled) {
    table.columns.push({
      label: 'Insurance Application',
      dataField: StoreStep.insurance,
      dataFormat: formatStatus,
      dataAlign: 'center',
    });
  }

  table.columns.push({
    label: 'Vehicle Sold',
    dataField: DealResult.SOLD,
    dataFormat: (_, { Deal }) => renderSoldCarCell({ Deal }),
    dataAlign: 'center',
  });

  return table;
};

export const getCSVFileName = (prefix, dealer, query) =>
  `${prefix}-${(dealer.name || '').replace(/[^a-zA-Z0-9]/g, '')}-${moment(
    query.startDate,
  ).format('YYYY.MM.DD')}-${moment(query.endDate).format('YYYY.MM.DD')}.csv`;

export const DEPOSIT_STATUS = {
  ACTIVE: 'active',
  VEHICLE_SOLD: 'vehicleSold',
  REFUNDED: 'refunded',
};

export const getDepositStatus = deposit => {
  if (deposit.refundId) {
    if (deposit.refundStatus === 'succeeded') return DEPOSIT_STATUS.REFUNDED;
  } else if (get(deposit, 'Car.status') === CarStatus.SOLD) {
    return DEPOSIT_STATUS.VEHICLE_SOLD;
  }
  return DEPOSIT_STATUS.ACTIVE;
};

export const getDepositsTableConfig = onRefundRequested => ({
  columns: [
    {
      label: 'Name',
      dataAlign: 'center',
      dataFormat: (cell, row) =>
        `${get(row, 'Deal.applicant.firstName') || ''} ${get(
          row,
          'Deal.applicant.lastName',
        ) || ''}`,
    },
    {
      label: 'Email',
      dataAlign: 'center',
      dataFormat: (cell, row) => get(row, 'Deal.applicant.email') || '',
    },
    {
      label: 'Phone',
      dataAlign: 'center',
      dataFormat: (cell, row) => get(row, 'Deal.applicant.phone') || '',
    },
    {
      label: 'Deposit Date',
      dataAlign: 'center',
      dataField: 'createdAt',
      dataFormat: formatDate,
    },
    {
      label: 'Vehicle',
      dataAlign: 'center',
      dataFormat: (cell, row) => get(row, 'Car.name') || '',
    },
    {
      label: 'Stock No.',
      dataAlign: 'center',
      dataFormat: (cell, row) => get(row, 'Car.stock') || '',
    },
    {
      label: 'Status',
      dataAlign: 'center',
      dataFormat: (cell, row) => {
        const status = getDepositStatus(row);
        return (
          <span className={`${kebabCase(status)}-deposit`}>
            {startCase(status)}
          </span>
        );
      },
    },
    {
      dataAlign: 'center',
      dataField: 'actions',
      dataFormat: (cell, row) => {
        if (!row.refundId) {
          return (
            <button
              className="btn btn-primary"
              onClick={() => onRefundRequested(row.id)}
            >
              Issue Refund
            </button>
          );
        }
        if (row.refundStatus !== 'succeeded') {
          return <span className="pending-refund">Pending Refund</span>;
        }
        return null;
      },
    },
  ],
});

export const getVirtualShowroomTable = () => ({
  columns: [
    {
      label: 'Salesperson',
      dataField: 'name',
    },
    {
      label: 'Times Online',
      dataField: 'timesOnlineCount',
      dataFormat: (_, row) =>
        // Make sure times online is positive if any requests were received
        Math.max(row.timesOnlineCount, row.requestCount > 0 ? 1 : 0),
      dataAlign: 'center',
    },
    {
      label: 'Connection Requests',
      dataFormat: (_, row) =>
        // Make sure the number of requests always >= number of accepted chats
        Math.max(row.requestCount, row.acceptedCount),
      dataAlign: 'center',
    },
    {
      label: 'Connections Accepted',
      dataField: 'acceptedCount',
      dataAlign: 'center',
    },
    {
      label: 'Video Calls',
      dataField: 'videoCallCount',
      dataAlign: 'center',
    },
    {
      label: 'Average Time Spent Connected',
      dataFormat: (_, row) => {
        const { acceptedCount, minutesConnected } = row;
        const average =
          acceptedCount > 0 ? Math.ceil(minutesConnected / acceptedCount) : 0;
        return getTimeLabelFromMinutes(average);
      },
      dataAlign: 'center',
    },
    {
      label: 'Vehicles Suggested',
      dataField: 'suggestionCount',
      dataAlign: 'center',
    },
    {
      label: 'Deposit Requests',
      dataField: 'depositCount',
      dataAlign: 'center',
    },
  ],
});
